import React, { useState, useEffect } from 'react';
import useAuthStore from '../store/authStore';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn, GridRow } from '../components/data/DataGrid';
import UsageTab from '../components/superAdmin/UsageTab';
import PersonasPanel from '../components/superAdmin/PersonasPanel';
import ToolServersPanel from '../components/superAdmin/ToolServersPanel';
import WorkflowsPanel from '../components/superAdmin/WorkflowsPanel';
import '../components/Modal.css';
import './SuperAdminPage.css';

interface Organization {
  id: number;
  name: string;
  subdomain?: string;
  createdAt: string;
  stats: {
    userCount: number;
    locationCount: number;
    financialRecordCount: number;
  };
}

interface CreateOrgForm {
  name: string;
  subdomain: string;
}

const SuperAdminPage: React.FC = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'organizations' | 'usage' | 'personas' | 'tool-servers' | 'workflows'>('organizations');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingOrg, setEditingOrg] = useState<any | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<any | null>(null);
  const [showCreateAdminForm, setShowCreateAdminForm] = useState<any | null>(null);
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  const [formData, setFormData] = useState<CreateOrgForm>({
    name: '',
    subdomain: ''
  });

  const [adminFormData, setAdminFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Check if user is Super Admin
  useEffect(() => {
    if (!user?.is_super_admin) {
      setError('Access denied. Super Admin privileges required.');
      setLoading(false);
      return;
    }
    fetchOrganizations();
  }, [user]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/super-admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch organizations: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log('API Response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON Parse Error. Response was:', responseText);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}...`);
      }
      if (data.success) {
        setOrganizations(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch organizations');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate subdomain from organization name
    if (name === 'name') {
      const subdomain = value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({
        ...prev,
        subdomain
      }));
    }
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate form
      if (!formData.name) {
        throw new Error('Organization name is required');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/super-admin/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to create organization: ${response.status}`);
      }

      if (data.success) {
        setSuccess(`Organization "${formData.name}" created successfully!`);
        setFormData({
          name: '',
          subdomain: ''
        });
        setShowCreateForm(false);
        fetchOrganizations(); // Refresh the list
      } else {
        throw new Error(data.message || 'Failed to create organization');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error creating organization:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrg) return;

    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/super-admin/organizations/${editingOrg.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: formData.name,
          subdomain: formData.subdomain
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update organization');
      }

      setSuccess('Organization updated successfully!');
      setEditingOrg(null);
      setFormData({ name: '', subdomain: '' });
      setShowCreateForm(false);
      fetchOrganizations(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to update organization');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOrganization = async (orgId: number) => {
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/super-admin/organizations/${orgId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete organization');
      }

      setSuccess('Organization deleted successfully!');
      setShowDeleteConfirm(null);
      fetchOrganizations(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete organization');
      setShowDeleteConfirm(null);
    }
  };

  const startEdit = (org: any) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      subdomain: org.subdomain
    });
    setShowCreateForm(true);
  };

  const cancelEdit = () => {
    setEditingOrg(null);
    setFormData({ name: '', subdomain: '' });
    setShowCreateForm(false);
  };

  const handleCreateOrgAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showCreateAdminForm) return;

    setCreatingAdmin(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/super-admin/organizations/${showCreateAdminForm.id}/create-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(adminFormData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create organization admin');
      }

      setSuccess(`Organization admin "${adminFormData.name}" created successfully for ${showCreateAdminForm.name}!`);
      setShowCreateAdminForm(null);
      setAdminFormData({ name: '', email: '', password: '' });
      fetchOrganizations(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to create organization admin');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const startCreateAdmin = (org: any) => {
    setShowCreateAdminForm(org);
    setAdminFormData({ name: '', email: '', password: '' });
  };

  const cancelCreateAdmin = () => {
    setShowCreateAdminForm(null);
    setAdminFormData({ name: '', email: '', password: '' });
  };

  const handleAdminInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAdminFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!user?.is_super_admin) {
    return (
      <div className="dashboard-page">
        <Sidebar />
        <div className="dashboard-main">
          <div className="dashboard-content">
            <div className="page-header">
              <h1>Access Denied</h1>
              <p>Super Admin privileges required to access this page.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
      <div className="page-header">
        <h1>Super Admin Dashboard</h1>
        <p>Platform management and organization oversight</p>
      </div>

      {/* Tab Navigation */}
      <div className="super-admin-tabs">
        <button
          onClick={() => setActiveTab('organizations')}
          className={`super-admin-tab-button ${activeTab === 'organizations' ? 'active' : ''}`}
        >
          🏢 Organizations
        </button>
        <button
          onClick={() => setActiveTab('tool-servers')}
          className={`super-admin-tab-button ${activeTab === 'tool-servers' ? 'active' : ''}`}
        >
          🔧 Tool Servers
        </button>
        <button
          onClick={() => setActiveTab('workflows')}
          className={`super-admin-tab-button ${activeTab === 'workflows' ? 'active' : ''}`}
        >
          📋 Workflows
        </button>
        <button
          onClick={() => setActiveTab('personas')}
          className={`super-admin-tab-button ${activeTab === 'personas' ? 'active' : ''}`}
        >
          🎭 Personas
        </button>
        <button
          onClick={() => setActiveTab('usage')}
          className={`super-admin-tab-button ${activeTab === 'usage' ? 'active' : ''}`}
        >
          📊 Usage
        </button>
      </div>

      {/* Organizations Tab Content */}
      {activeTab === 'organizations' && (
        <>
          <div className="header-actions" style={{ marginBottom: '20px' }}>
            <button 
              className={`${showCreateForm ? 'secondary-button' : 'primary-button'}`}
              onClick={() => setShowCreateForm(!showCreateForm)}
              disabled={creating}
            >
              {showCreateForm ? 'View Organizations' : 'Create Organization'}
            </button>
            <button 
              className="secondary-button"
              onClick={fetchOrganizations}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        </>
      )}

      {activeTab === 'organizations' && error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {activeTab === 'organizations' && success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="alert-close">×</button>
        </div>
      )}

      {activeTab === 'organizations' && showCreateForm ? (
        <div className="card">
          <div className="card-header">
            <h2>{editingOrg ? 'Edit Organization' : 'Create New Organization'}</h2>
          </div>
          <div className="card-content">
            <form onSubmit={editingOrg ? handleUpdateOrganization : handleCreateOrganization}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Organization Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., ABC Motors Group"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subdomain">Subdomain</label>
                  <input
                    type="text"
                    id="subdomain"
                    name="subdomain"
                    value={formData.subdomain}
                    onChange={handleInputChange}
                    placeholder="abc-motors (auto-generated)"
                  />
                  <small>Will be used for custom branding (optional)</small>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={creating}
                >
                  {creating ? (editingOrg ? 'Updating...' : 'Creating...') : (editingOrg ? 'Update Organization' : 'Create Organization')}
                </button>
                <button 
                  type="button" 
                  className="secondary-button"
                  onClick={editingOrg ? cancelEdit : () => setShowCreateForm(false)}
                  disabled={creating}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : activeTab === 'organizations' ? (
        <div className="card">
          <div className="card-header">
            <h2>Organizations ({organizations.length})</h2>
          </div>
          <div className="card-content">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading organizations...</p>
              </div>
            ) : organizations.length === 0 ? (
              <div className="empty-state">
                <h3>No Organizations Yet</h3>
                <p>Create your first organization to get started with multi-tenant management.</p>
                <button 
                  className="primary-button"
                  onClick={() => setShowCreateForm(true)}
                >
                  Create First Organization
                </button>
              </div>
            ) : (
              <DataGrid
                columns={[
                  {
                    field: 'name',
                    headerName: 'Organization Name',
                    width: 250,
                    renderCell: (row: GridRow) => (
                      <div>
                        <strong>{row.name}</strong>
                        <br />
                        <small style={{ color: '#888' }}>ID: {row.id}</small>
                      </div>
                    )
                  },
                  {
                    field: 'subdomain',
                    headerName: 'Subdomain',
                    width: 180,
                    renderCell: (row: GridRow) => (
                      row.subdomain ? (
                        <span style={{ 
                          background: '#0066cc', 
                          color: 'white', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px' 
                        }}>
                          {row.subdomain}
                        </span>
                      ) : (
                        <span style={{ color: '#666' }}>-</span>
                      )
                    )
                  },
                  {
                    field: 'userCount',
                    headerName: 'Users',
                    width: 100,
                    type: 'number'
                  },
                  {
                    field: 'locationCount',
                    headerName: 'Locations',
                    width: 120,
                    type: 'number'
                  },
                  {
                    field: 'recordCount',
                    headerName: 'Records',
                    width: 120,
                    type: 'number'
                  },
                  {
                    field: 'createdAt',
                    headerName: 'Created',
                    width: 140,
                    type: 'date',
                    valueFormatter: (value: string) => new Date(value).toLocaleDateString()
                  },
                  {
                    field: 'actions',
                    headerName: 'Actions',
                    width: 320,
                    renderCell: (row: GridRow) => (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button 
                          className="secondary-button"
                          onClick={() => startEdit(row)}
                          title="Update Organization"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Update
                        </button>
                        <button 
                          className="primary-button"
                          onClick={() => startCreateAdmin(row)}
                          title="Create Organization Admin"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Create Admin
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => setShowDeleteConfirm(row)}
                          title="Delete Organization"
                          style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                        >
                          Delete
                        </button>
                      </div>
                    )
                  }
                ]}
                rows={organizations.map(org => ({
                  id: org.id,
                  name: org.name,
                  subdomain: org.subdomain,
                  userCount: org.stats.userCount,
                  locationCount: org.stats.locationCount,
                  recordCount: org.stats.financialRecordCount,
                  createdAt: org.createdAt
                }))}
                height="100%"
                loading={loading}
                className="organizations-grid"
              />
            )}
          </div>
        </div>
      ) : null}

      {/* Tool Servers Tab Content */}
      {activeTab === 'tool-servers' && (
        <ToolServersPanel />
      )}

      {/* Workflows Tab Content */}
      {activeTab === 'workflows' && (
        <WorkflowsPanel />
      )}

      {/* Personas Tab Content */}
      {activeTab === 'personas' && (
        <PersonasPanel />
      )}

      {/* Usage Tab Content */}
      {activeTab === 'usage' && (
        <UsageTab />
      )}

      {/* Create Organization Admin Modal */}
      {activeTab === 'organizations' && showCreateAdminForm && (
        <div className="modal-overlay" onClick={() => setShowCreateAdminForm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Create Organization Admin</h2>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  Create an admin user for <strong>{showCreateAdminForm.name}</strong>
                </p>
              </div>
            </div>
            <form onSubmit={handleCreateOrgAdmin}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="admin-name">Admin Name *</label>
                  <input
                    type="text"
                    id="admin-name"
                    name="name"
                    value={adminFormData.name}
                    onChange={handleAdminInputChange}
                    placeholder="e.g., John Smith"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="admin-email">Email Address *</label>
                  <input
                    type="email"
                    id="admin-email"
                    name="email"
                    value={adminFormData.email}
                    onChange={handleAdminInputChange}
                    placeholder="e.g., admin@organization.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="admin-password">Password *</label>
                  <input
                    type="password"
                    id="admin-password"
                    name="password"
                    value={adminFormData.password}
                    onChange={handleAdminInputChange}
                    placeholder="Enter secure password"
                    minLength={8}
                    required
                  />
                  <small>Password must be at least 8 characters long</small>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  type="button"
                  className="secondary-button"
                  onClick={cancelCreateAdmin}
                  disabled={creatingAdmin}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="primary-button"
                  disabled={creatingAdmin}
                >
                  {creatingAdmin ? 'Creating Admin...' : 'Create Organization Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {activeTab === 'organizations' && showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the organization <strong>"{showDeleteConfirm.name}"</strong>?</p>
              <p style={{ color: '#ff6b6b', fontSize: '14px' }}>
                ⚠️ This action cannot be undone. All users, locations, and data associated with this organization will be permanently deleted.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="secondary-button"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="delete-button"
                onClick={() => handleDeleteOrganization(showDeleteConfirm.id)}
              >
                Delete Organization
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPage;